/* ============================================
   NIDOO — Global JavaScript
   Alpine.js stores, AJAX helpers, utilities
   ============================================ */

/* --- Safe localStorage (degrades gracefully, e.g. Safari private mode) --- */
const safeStorage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* storage unavailable — ignore */
    }
  },
  getJSON(key, fallback) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }
};
window.safeStorage = safeStorage;

// Keep AJAX-generated product links in the visitor's current language.
window.nidooLocalizedUrl = function nidooLocalizedUrl(rawUrl) {
  if (!rawUrl) return '#';
  const root = (window.nidooLocaleRoot || '/').replace(/\/$/, '');
  if (!root || root === '/') return rawUrl;
  try {
    const u = new URL(rawUrl, window.location.origin);
    if (u.origin !== window.location.origin) return rawUrl;
    if (u.pathname === root || u.pathname.startsWith(root + '/')) return u.pathname + u.search + u.hash;
    if (u.pathname.startsWith('/es/') || u.pathname.startsWith('/fr/')) return u.pathname + u.search + u.hash;
    return root + u.pathname + u.search + u.hash;
  } catch (_) {
    return rawUrl;
  }
};

/* --- AJAX Helpers --- */
class CartAPI {
  static async getCart() {
    const response = await fetch(window.routes.cart_url + '.js');
    const cart = await response.json();
    if (!response.ok || cart.status >= 400) {
      throw new Error(cart.description || cart.message || 'Could not load cart');
    }
    return cart;
  }

  static async addToCart(items) {
    const response = await fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) {
      throw new Error(data.description || data.message || 'Unable to add item to cart');
    }
    return data;
  }

  static async changeItem(key, quantity) {
    const response = await fetch(window.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) {
      throw new Error(data.description || data.message || 'Unable to update cart');
    }
    return data;
  }

  static async updateCart(updates) {
    const response = await fetch(window.routes.cart_update_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) {
      throw new Error(data.description || data.message || 'Unable to update cart');
    }
    return data;
  }
}

/* --- Utility Functions --- */
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  // Use the presentment currency already selected by Shopify Markets.
  if (!format) {
    const currency = window.themeCurrency || window.Shopify?.currency?.active;
    if (currency && Number.isFinite(Number(cents))) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency
        }).format(Number(cents) / 100);
      } catch (error) {
        // Retain the theme's original money formatting as a fallback.
      }
    }
  }
  const formatString = format || window.themeMoneyFormat || '${{amount}}';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;

    if (isNaN(number) || number == null) return '0';

    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsAmount = parts[1] ? decimal + parts[1] : '';
    return dollars + centsAmount;
  }

  let value = '';
  const match = formatString.match(placeholderRegex);
  switch (match ? match[1] : 'amount') {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ', '');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}

function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

function trapFocus(container) {
  const focusableElements = container.querySelectorAll(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        e.preventDefault();
      }
    }
  });

  firstFocusable?.focus();
}

/* --- Alpine.js Global Data & Stores --- */
document.addEventListener('alpine:init', () => {

  /* Keep Tab and Shift+Tab inside an open dialog; return focus on close.
     Uses Alpine's built-in directive API, without external focus plugins. */
  Alpine.directive('nidoo-focus', (el, { expression }, { effect, evaluateLater, cleanup }) => {
    const evaluateOpen = evaluateLater(expression);
    let active = false;
    let returnFocusTo = null;
    let frameId = null;
    const focusable = () => Array.from(el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(node => !node.closest('[inert]') && node.getClientRects().length > 0);
    const handleKeydown = event => {
      if (!active || event.key !== 'Tab') return;
      const elements = focusable();
      if (!elements.length) { event.preventDefault(); el.focus(); return; }
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && (document.activeElement === first || !el.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !el.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    el.addEventListener('keydown', handleKeydown);
    effect(() => evaluateOpen(open => {
      if (open && !active) {
        active = true;
        returnFocusTo = document.activeElement;
        frameId = requestAnimationFrame(() => {
          if (!active) return;
          const first = focusable()[0];
          if (first) first.focus({ preventScroll: true });
          else { el.setAttribute('tabindex', '-1'); el.focus({ preventScroll: true }); }
        });
      } else if (!open && active) {
        active = false;
        if (frameId !== null) cancelAnimationFrame(frameId);
        const previous = returnFocusTo;
        frameId = requestAnimationFrame(() => {
          if (previous && previous.isConnected && typeof previous.focus === 'function') previous.focus({ preventScroll: true });
        });
      }
    }));
    cleanup(() => {
      el.removeEventListener('keydown', handleKeydown);
      if (frameId !== null) cancelAnimationFrame(frameId);
    });
  });

  /* Cart Store */
  Alpine.store('cart', {
    items: [],
    itemCount: 0,
    totalPrice: 0,
    cartDiscounts: [],
    isOpen: false,
    isLoading: false,
    error: null,
    quantityTimers: {},
    pendingQuantityValues: {},
    deferredQuantityValues: {},

    hasQueuedQuantity(key) {
      return Object.prototype.hasOwnProperty.call(this.pendingQuantityValues, key) ||
        Object.prototype.hasOwnProperty.call(this.deferredQuantityValues, key);
    },

    get quantityUpdatePending() {
      return this.isLoading ||
        Object.keys(this.pendingQuantityValues).length > 0 ||
        Object.keys(this.deferredQuantityValues).length > 0;
    },

    queueQuantityChange(key, quantity) {
      if (!key) return;
      const nextQuantity = Math.max(0, Math.floor(Number(quantity) || 0));
      clearTimeout(this.quantityTimers[key]);
      this.pendingQuantityValues[key] = nextQuantity;
      this.quantityTimers[key] = setTimeout(() => {
        delete this.quantityTimers[key];
        const finalQuantity = this.pendingQuantityValues[key];
        delete this.pendingQuantityValues[key];
        if (finalQuantity !== undefined) this.updateQuantity(key, finalQuantity);
      }, 350);
    },

    async init() {
      const cart = await CartAPI.getCart();
      this.updateFromCart(cart);
    },

    open() {
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.isOpen = false;
      document.body.style.overflow = '';
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    },

    updateFromCart(cart) {
      if (cart.currency) window.themeCurrency = cart.currency;
      this.items = cart.items || [];
      this.itemCount = cart.item_count || 0;
      this.totalPrice = cart.total_price || 0;
      // Nov 1: order-level discounts (e.g. NAPCLUB10) so the drawer can explain why the subtotal is lower than the line prices.
      this.cartDiscounts = (cart.cart_level_discount_applications || [])
        .filter(discount => Number(discount.total_allocated_amount) > 0)
        .map(discount => ({ title: discount.title, amount: discount.total_allocated_amount }));
    },

    async addItem(variantId, quantity = 1, properties = {}) {
      this.isLoading = true;
      this.error = null;
      try {
        await CartAPI.addToCart([{ id: variantId, quantity, properties }]);
        const cart = await CartAPI.getCart();
        this.updateFromCart(cart);
        this.open();
        try { if (window.clarity) window.clarity('event', 'add_to_cart'); } catch (e) {}
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.error = error.message || (window.cartStrings && window.cartStrings.addError) || 'We couldn\'t add this to your cart. Try again or pick another size.';
        this.open();
      } finally {
        this.isLoading = false;
      }
    },

    async updateQuantity(key, quantity) {
      if (this.isLoading) {
        this.deferredQuantityValues[key] = quantity;
        return;
      }
      this.isLoading = true;
      this.error = null;
      try {
        const cart = await CartAPI.changeItem(key, quantity);
        this.updateFromCart(cart);
        if (window.location.pathname.endsWith('/cart') || window.location.pathname.endsWith('/cart/')) {
          window.location.reload();
          return;
        }
      } catch (error) {
        console.error('Error updating cart:', error);
        this.error = error.message || (window.cartStrings && window.cartStrings.error) || 'Your cart didn\'t update. Give it another try.';
        try {
          const actualCart = await CartAPI.getCart();
          this.updateFromCart(actualCart);
        } catch (refreshError) {
          console.error('Could not refresh cart after error:', refreshError);
        }
      } finally {
        this.isLoading = false;
        const nextKey = Object.keys(this.deferredQuantityValues)[0];
        if (nextKey !== undefined) {
          const nextQuantity = this.deferredQuantityValues[nextKey];
          delete this.deferredQuantityValues[nextKey];
          this.updateQuantity(nextKey, nextQuantity);
        }
      }
    },

    async removeItem(key) {
      clearTimeout(this.quantityTimers[key]);
      delete this.quantityTimers[key];
      delete this.pendingQuantityValues[key];
      await this.updateQuantity(key, 0);
    },

    get formattedTotal() {
      return formatMoney(this.totalPrice);
    }
  });

  /* Account Drawer Store */
  Alpine.store('account', {
    isOpen: false,

    open() {
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.isOpen = false;
      document.body.style.overflow = '';
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    }
  });

  /* Wishlist Store */
  Alpine.store('wishlist', {
    items: [],

    init() {
      this.items = safeStorage.getJSON('nidoo_wishlist', []);
    },

    add(productHandle) {
      if (!this.items.includes(productHandle)) {
        this.items.push(productHandle);
        this.save();
      }
    },

    remove(productHandle) {
      this.items = this.items.filter(h => h !== productHandle);
      this.save();
    },

    toggle(productHandle) {
      this.has(productHandle) ? this.remove(productHandle) : this.add(productHandle);
    },

    has(productHandle) {
      return this.items.includes(productHandle);
    },

    save() {
      safeStorage.set('nidoo_wishlist', JSON.stringify(this.items));
    },

    get count() {
      return this.items.length;
    }
  });

  /* Mobile Menu Store */
  Alpine.store('mobileMenu', {
    isOpen: false,

    open() {
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.isOpen = false;
      document.body.style.overflow = '';
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    }
  });

  /* Search Store */
  Alpine.store('search', {
    isOpen: false,
    query: '',
    results: null,
    isLoading: false,

    open() {
      this.isOpen = true;
    },

    close() {
      this.isOpen = false;
      this.query = '';
      this.results = null;
    },

    async search(query) {
      this.controller?.abort();
      const requestId = (this.searchRequestId || 0) + 1;
      this.searchRequestId = requestId;
      if (query.length < 2) {
        this.results = null;
        this.isLoading = false;
        return;
      }

      this.query = query;
      this.isLoading = true;
      const controller = new AbortController();
      this.controller = controller;

      try {
        const response = await fetch(
          `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,article,page&resources[limit]=4&section_id=predictive-search`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('Search failed');
        const text = await response.text();
        if (requestId !== this.searchRequestId) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup = html.querySelector('#shopify-section-predictive-search')?.innerHTML;
        this.results = resultsMarkup || null;
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.searchRequestId) return;
        console.error('Search error:', error);
        this.results = null;
      } finally {
        if (requestId === this.searchRequestId) this.isLoading = false;
      }
    }
  });

});

/* --- Lazy Load CSS --- */
function loadCSS(href) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

/* --- Intersection Observer for Animations --- */
document.addEventListener('DOMContentLoaded', () => {
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });
  }
});

/* --- Close drawers on Escape --- */
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    if (typeof Alpine !== 'undefined') {
      Alpine.store('cart')?.close();
      Alpine.store('account')?.close();
      Alpine.store('mobileMenu')?.close();
      Alpine.store('search')?.close();
    }
  }
});
